import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiFetch } from "../../utils";

// Async thunk to give credits
export const giveCredits = createAsyncThunk(
    "admin/giveCredits",
    async ({ UserId, amount }, { rejectWithValue }) => {
        try {
            const response = await apiFetch("api/a/addcredits", {
                method: "POST",
                body: JSON.stringify({ UserId, amount }),
                headers: { "Content-Type": "application/json" },
                idempotencyAction: "admin:addcredit"
            });
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message || "Failed to give credits");
        }
    }
);

// Async thunk to fetch all users (once)
export const fetchAllUsers = createAsyncThunk(
    "admin/fetchAccounts",
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiFetch("api/a/accounts");
            return await response.json(); // expect array of users
        } catch (error) {
            return rejectWithValue([]);
        }
    }
);

const adminSlice = createSlice({
    name: "admin",
    initialState: {
        creditId: "",
        creditUser: "",
        creditAmount: "",
        creditMessage: "",
        isGivingCredits: false,
        allUsers: [],        // all users fetched from API
        filteredUsers: [],   // filtered locally based on search input
        isLoadingUsers: false,
    },
    reducers: {
        setCreditId: (state, action) => {
            state.creditId = action.payload;
        },
        setCreditUser: (state, action) => {
            state.creditUser = action.payload;
        },
        setCreditAmount: (state, action) => {
            state.creditAmount = action.payload;
        },
        clearCreditMessage: (state) => {
            state.creditMessage = "";
        },
        filterUsers: (state, action) => {
            const query = action.payload.toLowerCase();
            state.filteredUsers = state.allUsers.filter(
                (u) => u.email?.toLowerCase().includes(query) || u.name?.toLowerCase().includes(query)
            );
        },
    },
    extraReducers: (builder) => {
        builder
            // give credits
            .addCase(giveCredits.pending, (state) => {
                state.isGivingCredits = true;
                state.creditMessage = "";
            })
            .addCase(giveCredits.fulfilled, (state, action) => {
                state.isGivingCredits = false;
                state.creditMessage = action.payload.message || "Credits added successfully";
                state.creditUser = "";
                state.creditAmount = "";
                state.creditId = "";
            })
            .addCase(giveCredits.rejected, (state, action) => {
                state.isGivingCredits = false;
                state.creditMessage = action.payload || "Failed to give credits";
            })

            // fetch all users
            .addCase(fetchAllUsers.pending, (state) => {
                state.isLoadingUsers = true;
            })
            .addCase(fetchAllUsers.fulfilled, (state, action) => {
                state.isLoadingUsers = false;
                state.allUsers = action.payload || [];
                state.filteredUsers = action.payload || [];
            })
            .addCase(fetchAllUsers.rejected, (state) => {
                state.isLoadingUsers = false;
                state.allUsers = [];
                state.filteredUsers = [];
            });
    },
});

export const { setCreditId, setCreditUser, setCreditAmount, clearCreditMessage, filterUsers } = adminSlice.actions;
export default adminSlice.reducer;
